import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Stack,
  Divider,
  IconButton,
  LinearProgress,
  Alert,
  Badge,
  Avatar,
  CircularProgress,
  Tooltip,
  Pagination,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Send as SendIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Message as MessageIcon,
  PictureAsPdf as PdfIcon,
  Dashboard as CardIcon,
  ViewCarousel as CarouselIcon,
  Image as ImageIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  Drafts as DraftIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { GET_RCS_TEMPLATES } from "../../config/urls";
import axios from "axios";
import BackButton from "../../common/back-button";
import { formatWithTzLabel } from "../../utils/dateUtils";

// Styled components
const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: "center",
  borderRadius: theme.shape.borderRadius * 2,
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[4],
  },
}));

const TemplateCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[4],
  },
}));

const TemplateList = ({
  bot,
  onBack,
  onCreateTemplate,
  onBroadcastTemplate,
  onEditTemplate,
  onSuccess,
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 20;
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterUseCase, setFilterUseCase] = useState("ALL");

  useEffect(() => {
    fetchTemplates();
  }, [bot.id, page]);

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = {
        "X-Tenant-ID": localStorage.getItem("tenant") || "",
      };

      const response = await axios.get(GET_RCS_TEMPLATES, {
        headers,
        params: {
          bot_id: bot.id,
          limit: limit,
          offset: (page - 1) * limit,
        },
      });

      const isPaginated = response.data && response.data.results !== undefined;
      const templatesData = isPaginated ? response.data.results : response.data;
      const countData = isPaginated
        ? response.data.count
        : templatesData.length;

      setTemplates(templatesData || []);
      setTotalCount(countData || 0);

      console.log("Templates API response:", response.data); // Debug log

      // The response has paginated format: {count, next, previous, results: [...]}
      // Extract the templates array from response.data.results
      console.log("Extracted templates:", templatesData); // Debug log
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch templates");
      console.error("Error fetching templates:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch =
      template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.text_message_content
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      template.card_title?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType =
      filterType === "ALL" || template.template_type === filterType;
    const matchesStatus =
      filterStatus === "ALL" || template.status === filterStatus;
    const matchesUseCase =
      filterUseCase === "ALL" || template.template_use_case === filterUseCase;

    return matchesSearch && matchesType && matchesStatus && matchesUseCase;
  });

  const getStatusConfig = (status) => {
    switch (status) {
      case "APPROVED":
        return {
          color: "success",
          icon: <CheckCircleIcon />,
          label: "Approved",
        };
      case "SUBMITTED":
        return { color: "warning", icon: <ScheduleIcon />, label: "Submitted" };
      case "REJECTED":
        return { color: "error", icon: <ErrorIcon />, label: "Rejected" };
      case "DRAFT":
      default:
        return { color: "default", icon: <DraftIcon />, label: "Draft" };
    }
  };

  const getTypeConfig = (type) => {
    switch (type) {
      case "text_message":
        return {
          icon: <MessageIcon />,
          label: "Text Message",
          color: "#1976d2",
        };
      case "text_message_with_pdf":
        return { icon: <PdfIcon />, label: "Text + PDF", color: "#d32f2f" };
      case "rich_card":
        return { icon: <CardIcon />, label: "Rich Card", color: "#388e3c" };
      case "carousel":
        return { icon: <CarouselIcon />, label: "Carousel", color: "#7b1fa2" };
      case "text_message_with_media":
        return {
          icon: <ImageIcon />,
          label: "Media Template",
          color: "#f57c00",
        };
      default:
        return { icon: <MessageIcon />, label: type, color: "#757575" };
    }
  };

  const getUseCaseColor = (useCase) => {
    switch (useCase) {
      case "Transactional":
        return "primary";
      case "Promotional":
        return "secondary";
      case "OTP":
        return "info";
      default:
        return "default";
    }
  };

  const stats = {
    total: templates.length,
    approved: templates.filter((t) => t.status === "APPROVED").length,
    submitted: templates.filter((t) => t.status === "SUBMITTED").length,
    draft: templates.filter((t) => t.status === "DRAFT").length,
    rejected: templates.filter((t) => t.status === "REJECTED").length,
  };

  const templateTypes = [
    { value: "text_message", label: "Text Message" },
    { value: "text_message_with_pdf", label: "Text + PDF" },
    { value: "rich_card", label: "Rich Card" },
    { value: "carousel", label: "Carousel" },
    { value: "text_message_with_media", label: "Media Template" },
  ];

  const statusOptions = [
    { value: "DRAFT", label: "Draft" },
    { value: "SUBMITTED", label: "Submitted" },
    { value: "APPROVED", label: "Approved" },
    { value: "REJECTED", label: "Rejected" },
  ];

  const useCaseOptions = [
    { value: "Transactional", label: "Transactional" },
    { value: "Promotional", label: "Promotional" },
    { value: "OTP", label: "OTP" },
  ];

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading templates...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ mt: 2 }}
        action={
          <Button color="inherit" size="small" onClick={fetchTemplates}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  const permissionsFromStorage = JSON.parse(
    localStorage.getItem("permissions") || "[]",
  );
  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 4 }}
      >
        <Box>
          <BackButton onClick={onBack} />
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            Templates for {bot.bot_name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and manage messaging templates for your RCS bot
          </Typography>
        </Box>

        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchTemplates}
            disabled={loading}
          >
            Refresh
          </Button>
          {permissionsFromStorage.includes("rcs_template.create") ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateTemplate}
            >
              Create Template
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard elevation={1}>
            <Typography
              variant="h3"
              component="div"
              sx={{ fontWeight: 700, mb: 1 }}
            >
              {stats.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Templates
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            elevation={1}
            sx={{ borderTop: 4, borderColor: "success.main" }}
          >
            <Typography
              variant="h3"
              component="div"
              sx={{ fontWeight: 700, mb: 1, color: "success.main" }}
            >
              {stats.approved}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Approved
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            elevation={1}
            sx={{ borderTop: 4, borderColor: "warning.main" }}
          >
            <Typography
              variant="h3"
              component="div"
              sx={{ fontWeight: 700, mb: 1, color: "warning.main" }}
            >
              {stats.submitted}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Submitted
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            elevation={1}
            sx={{ borderTop: 4, borderColor: "grey.500" }}
          >
            <Typography
              variant="h3"
              component="div"
              sx={{ fontWeight: 700, mb: 1, color: "grey.700" }}
            >
              {stats.draft}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Draft
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            elevation={1}
            sx={{ borderTop: 4, borderColor: "error.main" }}
          >
            <Typography
              variant="h3"
              component="div"
              sx={{ fontWeight: 700, mb: 1, color: "error.main" }}
            >
              {stats.rejected}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Rejected
            </Typography>
          </StatCard>
        </Grid>
      </Grid>

      {/* Filters Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel id="type-filter-label">Template Type</InputLabel>
              <Select
                labelId="type-filter-label"
                value={filterType}
                label="Template Type"
                onChange={(e) => setFilterType(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="ALL">All Types</MenuItem>
                {templateTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel id="use-case-filter-label">Use Case</InputLabel>
              <Select
                labelId="use-case-filter-label"
                value={filterUseCase}
                label="Use Case"
                onChange={(e) => setFilterUseCase(e.target.value)}
              >
                <MenuItem value="ALL">All Use Cases</MenuItem>
                {useCaseOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="ALL">All Status</MenuItem>
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <Paper sx={{ p: 8, textAlign: "center", borderRadius: 2 }}>
          <MessageIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
          <Typography variant="h6" gutterBottom color="text.secondary">
            {templates.length === 0
              ? "No templates created yet"
              : "No templates match your filters"}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {templates.length === 0
              ? "Get started by creating your first template"
              : "Try adjusting your search or filters"}
          </Typography>
          {templates.length === 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateTemplate}
              size="large"
            >
              Create Your First Template
            </Button>
          )}
        </Paper>
      ) : (
        <>
          <Grid container spacing={3}>
            {filteredTemplates.map((template) => {
              const statusConfig = getStatusConfig(template.status);
              const typeConfig = getTypeConfig(template.template_type);

              const statusChip = (
                <Chip
                  icon={statusConfig.icon}
                  label={statusConfig.label}
                  color={statusConfig.color}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              );

              return (
                <Grid item xs={12} sm={6} lg={4} key={template.id}>
                  <TemplateCard elevation={2}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Header */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        sx={{ mb: 2 }}
                      >
                        <Badge
                          badgeContent={
                            <Avatar
                              sx={{
                                width: 24,
                                height: 24,
                                bgcolor: typeConfig.color,
                                fontSize: "0.75rem",
                              }}
                            >
                              {typeConfig.icon}
                            </Avatar>
                          }
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                        >
                          <Chip
                            label={typeConfig.label}
                            size="small"
                            sx={{
                              bgcolor: `${typeConfig.color}15`,
                              color: typeConfig.color,
                              fontWeight: 500,
                              ml: 3,
                            }}
                          />
                        </Badge>

                        {/* RIGHT SIDE: Status Chip (With Tooltip if Rejected) */}
                        {template.status === "REJECTED" &&
                        template.description ? (
                          <Tooltip
                            title={`Reason: ${template.description}`}
                            arrow
                            placement="top"
                          >
                            {/* We wrap in a span to ensure Tooltip works even if Chip is disabled or custom */}
                            <Box component="span">{statusChip}</Box>
                          </Tooltip>
                        ) : (
                          statusChip
                        )}
                      </Stack>

                      {/* Template Name */}
                      <Typography variant="h6" component="h3" gutterBottom>
                        {template.name}
                      </Typography>

                      {/* Use Case */}
                      {template.template_use_case && (
                        <Chip
                          label={template.template_use_case}
                          color={getUseCaseColor(template.template_use_case)}
                          size="small"
                          sx={{ mb: 2 }}
                        />
                      )}

                      {/* Content Preview */}
                      {/* Content Preview */}
                      <Box sx={{ my: 2, flexGrow: 1 }}>
                        {/* 1. TEXT MESSAGE CONTENT */}
                        {template.text_message_content && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 1,
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              bgcolor: "#f5f5f5",
                              p: 1,
                              borderRadius: 1,
                            }}
                          >
                            {template.text_message_content}
                          </Typography>
                        )}

                        {/* 2. RICH CARD PREVIEW */}
                        {template.card_title && (
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.5,
                              bgcolor: "grey.50",
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              display: "flex",
                              gap: 1,
                            }}
                          >
                            {template.media_file && (
                              <Box
                                sx={{
                                  width: 50,
                                  height: 50,
                                  borderRadius: 1,
                                  overflow: "hidden",
                                  flexShrink: 0,
                                }}
                              >
                                <img
                                  src={template.media_file}
                                  alt=""
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </Box>
                            )}
                            <Box>
                              <Typography variant="subtitle2" lineHeight={1.2}>
                                {template.card_title}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {template.card_description}
                              </Typography>
                            </Box>
                          </Paper>
                        )}

                        {/* 3. CAROUSEL PREVIEW (Fix for missing carousels) */}
                        {template.template_type === "carousel" &&
                          template.carousel_list &&
                          template.carousel_list.length > 0 && (
                            <Paper
                              elevation={0}
                              sx={{
                                p: 1.5,
                                bgcolor: "#f3e5f5", // Slight purple tint for carousel
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ mb: 1 }}
                              >
                                <CarouselIcon
                                  fontSize="small"
                                  color="secondary"
                                />
                                <Typography variant="caption" fontWeight="bold">
                                  {template.carousel_list.length} Cards
                                </Typography>
                              </Stack>

                              {/* Preview First Card */}
                              <Box sx={{ display: "flex", gap: 1 }}>
                                {(template.carousel_list[0].media_file ||
                                  template.carousel_list[0].media_url) && (
                                  <Box
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: 1,
                                      overflow: "hidden",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <img
                                      src={
                                        template.carousel_list[0].media_file ||
                                        template.carousel_list[0].media_url
                                      }
                                      alt=""
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  </Box>
                                )}
                                <Box>
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    display="block"
                                  >
                                    {template.carousel_list[0].title ||
                                      template.carousel_list[0].card_title}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 1,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {template.carousel_list[0].description ||
                                      template.carousel_list[0]
                                        .card_description}
                                  </Typography>
                                </Box>
                              </Box>
                            </Paper>
                          )}
                      </Box>

                      {/* Features */}
                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        {template.suggestions &&
                          template.suggestions.length > 0 && (
                            <Chip
                              label={`${template.suggestions.length} suggestions`}
                              size="small"
                              variant="outlined"
                            />
                          )}

                        {template.carousel_list &&
                          template.carousel_list.length > 0 && (
                            <Chip
                              label={`${template.carousel_list.length} cards`}
                              size="small"
                              variant="outlined"
                            />
                          )}

                        {template.media_file && (
                          <Chip label="Media" size="small" variant="outlined" />
                        )}
                      </Stack>

                      {/* Created Date */}
                      <Typography variant="caption" color="text.secondary">
                        Created:{" "}
                        {formatWithTzLabel(template.created_at)}
                      </Typography>
                    </CardContent>

                    <Divider />

                    <CardActions sx={{ justifyContent: "space-between", p: 2 }}>
                      {permissionsFromStorage.includes("rcs_template.edit") && (
                        <IconButton
                          size="small"
                          onClick={() => onEditTemplate(template)}
                          title="Edit template"
                        >
                          <EditIcon />
                        </IconButton>
                      )}

                      {template.status === "APPROVED" && (
                        <Button
                          size="small"
                          startIcon={<SendIcon />}
                          onClick={() => onBroadcastTemplate(template)}
                          variant="contained"
                          color="primary"
                        >
                          Broadcast
                        </Button>
                      )}
                    </CardActions>
                  </TemplateCard>
                </Grid>
              );
            })}
          </Grid>

          {/* Results Count & Pagination */}
          <Box
            sx={{
              mt: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {filteredTemplates.length} templates on this page
                (Total: {totalCount})
              </Typography>

              {searchTerm ||
              filterType !== "ALL" ||
              filterStatus !== "ALL" ||
              filterUseCase !== "ALL" ? (
                <Button
                  size="small"
                  onClick={() => {
                    setSearchTerm("");
                    setFilterType("ALL");
                    setFilterStatus("ALL");
                    setFilterUseCase("ALL");
                  }}
                >
                  Clear all filters
                </Button>
              ) : null}
            </Box>

            {/* Pagination Controls */}
            {totalCount > limit && (
              <Pagination
                count={Math.ceil(totalCount / limit)}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default TemplateList;
